import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import {
  LoanTermType,
  LoanTermValueOptions,
  MIN_LOAN_AMOUNT_BUSINESS_LOAN_AND_OVERDRAFT,
  OverdraftPaymentBreakupDialogData,
  PaymentFrequencyValue,
  PaymentFrequencyValueOptions,
  Quote,
  SliderComponentValue, YesNoValue, calculateBusinessOverdraftFees,
} from '@portal-workspace/grow-shared-library';
import { Subscription } from 'rxjs';
import { setupUntilDestroy } from '@portal-workspace/grow-ui-library';
import { PortalHotToastService } from '@portal-workspace/grow-ui-library';
import { tap } from 'rxjs/operators';
import {
  doMarkAll,
  markTriggerSubject
} from '@portal-workspace/grow-ui-library';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ApplicationDialogService } from '@portal-workspace/grow-ui-library';
import { ActivatedRoute, Router } from '@angular/router';
import { ApplicationService } from '../../service/application.service';
import { RatecardDetails, } from '@portal-workspace/grow-shared-library';
import { LoanTermValue } from '@portal-workspace/grow-shared-library';
import { navigationUrlForApplications } from '../../service/navigation-urls';
import { MatButtonModule } from '@angular/material/button';

import { PaymentFrequencySelectionComponent } from '@portal-workspace/grow-ui-library';
import { YesNoComponent } from '@portal-workspace/grow-ui-library';
import { MarkDirective } from '@portal-workspace/grow-ui-library';
import { MessageBoxComponent } from '@portal-workspace/grow-ui-library';
import { CurrencyInputComponent } from '@portal-workspace/grow-ui-library';
import { FlexModule } from '@angular/flex-layout/flex';
import moment from 'moment';
import numeral from 'numeral';
import { DisableControlDirective } from '@portal-workspace/grow-ui-library';

// ZEN-818 follow-up: standalone calculators lack application context, so we offer a
// coarse TIB select that maps to a conservative gstAgeMonths floor for the tier resolver.
// Overdraft requires >= 36 months TIB; below that the customer is not eligible.
export type TibSelectionValue = { id: '60+' | '36-59' | '<36'; name: string; gstAgeMonths: number; eligible: boolean } | null;
const TIB_SELECTION_OPTIONS: Exclude<TibSelectionValue, null>[] = [
  { id: '60+',   name: '5+ years (≥ 60 months)', gstAgeMonths: 60, eligible: true },
  { id: '36-59', name: '3–5 years (36–59 months)', gstAgeMonths: 36, eligible: true },
  { id: '<36',   name: 'Less than 3 years (< 36 months)', gstAgeMonths: 0, eligible: false },
];

const NOT_ELIGIBLE_MESSAGE = 'Customer is not eligible for a Dynamoney Overdraft';

@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
  templateUrl: './business-overdraft-calculator.page.html',
  styleUrls: ['business-overdraft-calculator.page.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    FlexModule,
    MarkDirective,
    MatButtonModule,
    YesNoComponent,
    PaymentFrequencySelectionComponent,
    CurrencyInputComponent,
    DisableControlDirective,
    MatSelectModule,
    MatFormFieldModule,
    MessageBoxComponent,
],
})

export class BusinessOverdraftCalculatorPage implements OnInit {

  maxLimit: number = 500000;
  minLimit: number = MIN_LOAN_AMOUNT_BUSINESS_LOAN_AND_OVERDRAFT;
  brokerageDollar?: number;
  isDisable = true

  subscriptions: Subscription[] = [];

  markTriggerSubject = markTriggerSubject;

  formGroupCalculator!: FormGroup<{
    loanAmount: FormControl<SliderComponentValue>,
    anticipatedDrawdownAmount: FormControl<SliderComponentValue>,
    loanTerms: FormControl<LoanTermValue>,
    paymentFrequency: FormControl<PaymentFrequencyValue>,
    propertyOwner: FormControl<YesNoValue>,
    adverseOnFile: FormControl<YesNoValue>,
    equifaxScore: FormControl<YesNoValue>,
    tib: FormControl<TibSelectionValue>,
    additionalCommission: FormControl<YesNoValue>,
  }>;

  formControlLoanTerms!: FormControl<LoanTermValue>;
  formControlPaymentFrequency!: FormControl<PaymentFrequencyValue>;
  formControlPropertyOwner!: FormControl<YesNoValue>;
  formControlAdverseOnFile!: FormControl<YesNoValue>;
  formControlEquifaxScoreAboveThreshold!: FormControl<YesNoValue>;
  formControlLoanAmount!: FormControl<SliderComponentValue>;
  formControlAnticipatedDrawdownAmount!: FormControl<SliderComponentValue>;
  formControlTib!: FormControl<TibSelectionValue>;
  tibOptions = TIB_SELECTION_OPTIONS;
  formControlAdditionalCommission!: FormControl<YesNoValue>;

  rateCard!: RatecardDetails;

  tibCompareFn = (a: TibSelectionValue, b: TibSelectionValue) => a?.id === b?.id;
  notEligibleMessage = NOT_ELIGIBLE_MESSAGE;

  get tibNotEligible(): boolean {
    const tib = this.formControlTib.value;
    return !!tib && !tib.eligible;
  }

  constructor(
    private formBuilder: FormBuilder,
    private toastService: PortalHotToastService,
    private applicationDialogService: ApplicationDialogService,
    private activatedRoute: ActivatedRoute,
    private appService: ApplicationService,
    private router: Router) {

    this.initCalculator();
  }

  ngOnInit() {
    setupUntilDestroy(this);
    this.rateCard = (this.activatedRoute.snapshot.data as any).ratecard;

    this.subscriptions.push(this.formControlPropertyOwner.valueChanges.pipe(
      tap(r => {
        this.updateMaxLimit();
      })
    ).subscribe());

    this.subscriptions.push(this.formControlEquifaxScoreAboveThreshold.valueChanges.pipe(
      tap(() => {
        this.updateMaxLimit();
      })
    ).subscribe());

  }

  private updateMaxLimit() {
    const isPropertyOwner = this.formControlPropertyOwner.value;
    if (isPropertyOwner) {
      this.maxLimit = 500000;
    } else {
      this.maxLimit = 100000;
    }
  }

  initCalculator() {

    this.formControlLoanAmount = this.formBuilder.control(null, [Validators.required]);
    this.formControlAnticipatedDrawdownAmount = this.formBuilder.control(null);
    this.formControlLoanTerms = this.formBuilder.control(LoanTermValueOptions[2], [Validators.required]);
    this.formControlPaymentFrequency = this.formBuilder.control(PaymentFrequencyValueOptions[2], [Validators.required]);
    this.formControlPropertyOwner = this.formBuilder.control(true, [Validators.required]);
    this.formControlAdverseOnFile = this.formBuilder.control(false, [Validators.required]);
    this.formControlEquifaxScoreAboveThreshold = this.formBuilder.control(true, [Validators.required]);
    this.formControlTib = this.formBuilder.control(TIB_SELECTION_OPTIONS[0], [Validators.required]);
    this.formControlAdditionalCommission = this.formBuilder.control(false, [Validators.required]);

    this.formGroupCalculator = this.formBuilder.group({
      loanAmount: this.formControlLoanAmount,
      anticipatedDrawdownAmount: this.formControlAnticipatedDrawdownAmount,
      loanTerms: this.formControlLoanTerms,
      paymentFrequency: this.formControlPaymentFrequency,
      propertyOwner: this.formControlPropertyOwner,
      adverseOnFile: this.formControlAdverseOnFile,
      equifaxScore: this.formControlEquifaxScoreAboveThreshold,
      tib: this.formControlTib,
      additionalCommission: this.formControlAdditionalCommission,
    });
  }

  onCalculateEstimation(event: Event) {
    markTriggerSubject(this.formGroupCalculator).next(true);
    doMarkAll(this.formGroupCalculator);
    if (this.tibNotEligible) {
      this.applicationDialogService.openAlertDialog({
        message: 'Not eligible',
        subMessage: NOT_ELIGIBLE_MESSAGE,
      });
    } else if (this.formGroupCalculator.invalid) {
      this.applicationDialogService.openAlertDialog({
        message: 'Missing Information',
        subMessage: 'Make sure all fields across tabs are filled in.',
      });
    } else {
      this.subscriptions.push(this.applicationDialogService.openBusinessOverdraftQuotationDialog({
        loadFn: () => {
          return this.calculateEstimation();
        },
        isQuote: true,
        hideBrokerage: false,
      }).afterClosed()
        .pipe(
          tap(r => {
            if (r && r.acceptedQuotation) {
              const quoteData = this.saveQuoteData();
              this.appService.saveQuote(quoteData).subscribe(async res => {
                if (res.status) {
                  await this.router.navigate(navigationUrlForApplications())
                } else {
                  this.applicationDialogService.openAlertDialog({
                    message: `Error`,
                    subMessage: "Something is wrong. Try again."
                  });
                }
              })
            }
          })
        ).subscribe());
    }
  }

  private saveQuoteData(): Quote {
    const loanCalValues = this.formGroupCalculator.getRawValue();
    const quoteValue: Quote = {
      LoanAmount: (loanCalValues.loanAmount ?? '0').toString(),
      RepaymentFrequency: loanCalValues.paymentFrequency?.type!,
      LoanTerm: parseInt(loanCalValues.loanTerms?.type ?? '0'),
      BrokerageAmount: this.brokerageDollar ?? 0,
      QuoteAsJson: encodeURI(JSON.stringify(loanCalValues)),
    };
    return quoteValue
  }

  private calculateEstimation(): OverdraftPaymentBreakupDialogData {
    const date = moment().add(1, 'day');
    const loanTermValue: LoanTermValue = this.formControlLoanTerms.value;
    const loanTermType: LoanTermType | null = loanTermValue?.type ?? null;
    const propertyOwner = this.formControlPropertyOwner.value ?? false;
    const adverseOnFile = this.formControlAdverseOnFile.value ?? false;
    const equifaxDirectorScoreAbove700 = this.formControlEquifaxScoreAboveThreshold.value ?? false;
    const gstAgeMonths = this.formControlTib.value?.gstAgeMonths ?? 60;
    const facilityLimit = this.formControlLoanAmount.value ?? 0;
    const additionalCommission = this.formControlAdditionalCommission.value ?? false;
    const anticipatedDrawdownAmount = numeral(this.formControlAnticipatedDrawdownAmount.value).value() ?? 0;

    const r = calculateBusinessOverdraftFees(date, {
      type: 'BusinessOverdraft',
      loanTermType,
      propertyOwner,
      adverseOnFile,
      equifaxDirectorScoreAbove700,
      gstAgeMonths,
      rateCard: this.rateCard,
      facilityLimit,
      additionalCommission,
      anticipatedDrawdownAmount,
    });

    this.brokerageDollar = r.term.brokerageDollar;
    return {
      term: r.term,
    };
  }

}
